import { Anchor, Icon, WarningFilledSVG } from "@newsela/angelou";
import PropTypes from "prop-types";

import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import {
  containerCss,
  contentCss,
  headingCss,
  iconCss,
  rootCss,
  textCss,
} from "./ContentSettingsAdvisory.style";
import getAdvisoryDetails from "./getAdvisoryDetails";

export const reasonTypes = {
  DISTRICT_ADVISORY: "DISTRICT_ADVISORY",
  SCHOOL_ADVISORY: "SCHOOL_ADVISORY",
};

const propTypes = {
  isStudent: PropTypes.bool,
  visibilityMarkers: PropTypes.arrayOf(
    PropTypes.shape({
      block_teacher_view: PropTypes.bool,
      flagged_at_my_admin_level: PropTypes.bool,
      grade_maximum: PropTypes.number,
      grade_minimum: PropTypes.number,
      learn_more_url: PropTypes.string,
      message: PropTypes.string,
      reason: PropTypes.oneOf(Object.values(reasonTypes)).isRequired,
    }),
  ),
};

const renderInfoMessage = (marker) => {
  const {
    message: customMessage,
    flagged_at_my_admin_level: flaggedAtMyAdminLevel,
  } = marker;

  if (flaggedAtMyAdminLevel) return null;

  const defaultMessage =
    "Your admin has flagged this content with an advisory label. " +
    "It may be in violation of district policies or curricular goals. " +
    "For more information, please contact a district leader.";
  const message = customMessage || defaultMessage;

  return <p css={textCss}>{message}</p>;
};

const renderLearnMoreLink = (marker) => {
  const {
    flagged_at_my_admin_level: flaggedAtMyAdminLevel,
    learn_more_url: learnMoreUrl,
    message: customMessage,
  } = marker;

  return !flaggedAtMyAdminLevel && customMessage && learnMoreUrl ? (
    <>
      {" "}
      <Anchor isInline href={learnMoreUrl}>
        Learn more
      </Anchor>
    </>
  ) : (
    ""
  );
};

const getHeaderText = (marker) => {
  const {
    flagged_at_my_admin_level: flaggedAtMyAdminLevel,
    reason: markerReason,
  } = marker;

  if (markerReason === reasonTypes.DISTRICT_ADVISORY && flaggedAtMyAdminLevel) {
    return "Flagged at your district";
  }
  if (markerReason === reasonTypes.SCHOOL_ADVISORY && flaggedAtMyAdminLevel) {
    return "Flagged at your school";
  }
  return "Important message";
};

const renderBanner = (markers) => {
  if (!markers.length) return null;

  const headerText = getHeaderText(markers[0]);

  return (
    <div css={rootCss}>
      <div>
        <Icon
          __cssFor={{ root: iconCss }}
          isDecorative
          SvgComponent={WarningFilledSVG}
        />
      </div>
      <div css={contentCss}>
        <h2 css={headingCss}>{headerText}</h2>
        {renderInfoMessage(markers[0])}
        <p css={textCss}>
          {getAdvisoryDetails(markers)}
          {renderLearnMoreLink(markers[0])}
        </p>
      </div>
    </div>
  );
};

const ContentSettingsAdvisory = ({ isStudent, visibilityMarkers }) => {
  const { isStudentPreviewOn } = useStudentPreviewContext();

  const hideAdvisory =
    isStudent || isStudentPreviewOn || !visibilityMarkers?.length;

  if (hideAdvisory) return null;

  const markersByReason = {
    DISTRICT_ADVISORY: visibilityMarkers.filter(
      (marker) => marker.reason === reasonTypes.DISTRICT_ADVISORY,
    ),
    SCHOOL_ADVISORY: visibilityMarkers.filter(
      (marker) => marker.reason === reasonTypes.SCHOOL_ADVISORY,
    ),
  };

  return (
    <div css={containerCss}>
      {renderBanner(markersByReason.DISTRICT_ADVISORY)}
      {renderBanner(markersByReason.SCHOOL_ADVISORY)}
    </div>
  );
};

ContentSettingsAdvisory.propTypes = propTypes;

export default ContentSettingsAdvisory;
